import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { User, KeyRound, Folder as FolderIcon, Globe, RefreshCcw, Eye, EyeOff } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { formatBytes, formatDate } from '../lib/utils';
import toast from 'react-hot-toast';
import { StatefulButton } from '../components/aceternity/StatefulButton';
import { Lamp } from '../components/aceternity/Lamp';

export function Settings() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);

  const usage = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.users.usage(),
    enabled: !!user,
  });

  const shares = useQuery({
    queryKey: ['my-shares'],
    queryFn: () => api.shares.list(),
    enabled: !!user,
  });

  const saveName = async () => {
    try {
      const me = await api.users.updateMe({ name });
      setUser(me);
      toast.success('已保存');
    } catch (e: any) {
      toast.error(e.message || '保存失败');
      throw e;
    }
  };

  const revokeShare = async (code: string) => {
    if (!confirm('撤销该分享链接？')) return;
    await api.shares.revoke(code);
    shares.refetch();
    toast.success('已撤销');
  };

  const changePassword = async () => {
    if (newPassword.length < 8) { toast.error('新密码至少 8 位'); return; }
    if (newPassword !== confirmPassword) { toast.error('两次输入的新密码不一致'); return; }
    setChangingPwd(true);
    try {
      await api.users.changePassword({ oldPassword, newPassword });
      toast.success('密码修改成功');
      setOldPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (e: any) {
      toast.error(e.message || '修改失败');
    } finally {
      setChangingPwd(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5 relative">
      <Lamp />
      <div className="animate-fadeInUp">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono tracking-[0.35em] text-accent-violet/80">SETTINGS</span>
          <span className="h-px w-10 bg-gradient-to-r from-accent-violet to-transparent" />
        </div>
        <h1 className="text-3xl font-black neon-text tracking-tight mt-0.5">设置</h1>
      </div>

      {/* 账户 */}
      <section className="bg-card/80 border border-border-soft rounded-2xl p-6 animate-fadeInUp soft-shadow">
        <h3 className="font-semibold text-text mb-4 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10"><User className="w-4 h-4 text-primary" /></span>
          账户信息
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-muted mb-1.5">邮箱</label>
            <input readOnly value={user?.email || ''} className="w-full px-3 py-2.5 bg-bg/60 border border-border rounded-xl text-text opacity-70" />
          </div>
          <div>
            <label className="block text-sm text-text-muted mb-1.5">昵称</label>
            <div className="flex gap-2">
              <input value={name} onChange={(e) => setName(e.target.value)} className="flex-1 px-3 py-2.5 bg-bg/60 border border-border rounded-xl text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all" />
              <StatefulButton
                onAction={saveName}
                successText="已保存"
                className="px-4 py-2 brand-gradient text-white btn-press soft-shadow"
              >
                保存
              </StatefulButton>
            </div>
          </div>
          <div>
            <label className="block text-sm text-text-muted mb-1.5">角色</label>
            <span className="px-2.5 py-1 text-xs rounded-full bg-primary/10 text-primary border border-primary/20">{user?.role === 'admin' ? '管理员' : '成员'}</span>
          </div>
        </div>
      </section>

      {/* 存储 */}
      <section className="bg-card/80 border border-border-soft rounded-2xl p-6 animate-fadeInUp soft-shadow">
        <h3 className="font-semibold text-text mb-4 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10"><FolderIcon className="w-4 h-4 text-primary" /></span>
          存储空间
        </h3>
        <div className="flex justify-between text-sm text-text-muted mb-2">
          <span>已用 {formatBytes(usage.data?.used ?? 0)}</span>
          <span>总配额 {formatBytes(usage.data?.quota ?? 0)}</span>
        </div>
        <div className="h-2 bg-bg-secondary rounded-full overflow-hidden">
          <div className="h-full brand-gradient transition-all duration-500 ease-out rounded-full" style={{ width: `${Math.min(100, ((usage.data?.used ?? 0) / (usage.data?.quota ?? 1)) * 100)}%` }} />
        </div>
      </section>

      {/* 修改密码 */}
      <section className="bg-card/80 border border-border-soft rounded-2xl p-6 animate-fadeInUp soft-shadow">
        <h3 className="font-semibold text-text mb-4 flex items-center gap-2"><span className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10"><KeyRound className="w-4 h-4 text-primary" /></span> 修改密码</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-muted mb-1">当前密码</label>
            <div className="relative">
              <input type={showOld ? 'text' : 'password'} value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} autoComplete="current-password"
                className="w-full px-3 py-2 bg-bg border border-border rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 pr-10" placeholder="输入当前密码" />
              <button type="button" onClick={() => setShowOld(!showOld)} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text" aria-label="显示/隐藏">
                {showOld ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm text-text-muted mb-1">新密码</label>
            <div className="relative">
              <input type={showNew ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password"
                className="w-full px-3 py-2 bg-bg border border-border rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 pr-10" placeholder="至少 8 位" />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text" aria-label="显示/隐藏">
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm text-text-muted mb-1">确认新密码</label>
            <input type={showNew ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password"
              onKeyDown={(e) => e.key === 'Enter' && changePassword()}
              className="w-full px-3 py-2 bg-bg border border-border rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="再次输入新密码" />
          </div>
          <button onClick={changePassword} disabled={changingPwd}
            className="px-4 py-2 bg-primary hover:bg-primary-hover disabled:opacity-50 text-white rounded-lg">
            {changingPwd ? '提交中...' : '确认修改密码'}
          </button>
        </div>
      </section>

      {/* 分享管理 */}
      <section className="bg-card/80 border border-border-soft rounded-2xl p-6 animate-fadeInUp soft-shadow">
        <h3 className="font-semibold text-text mb-4 flex items-center gap-2"><span className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10"><Globe className="w-4 h-4 text-primary" /></span> 我的分享 ({shares.data?.length ?? 0})</h3>
        {shares.isLoading ? <p className="text-text-muted text-sm">加载中...</p>
          : !shares.data?.length ? <p className="text-text-muted text-sm">暂无分享链接</p>
          : (
            <div className="space-y-2">
              {shares.data.map((s: any) => (
                <div key={s.code} className="flex items-center gap-3 bg-bg rounded-lg px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-text truncate">{s.nodeName}</p>
                    <p className="text-xs text-text-muted flex items-center gap-2">
                      {s.protected && <span className="flex items-center gap-0.5"><KeyRound className="w-3 h-3" /> 密码</span>}
                      <span>下载 {s.downloadCount}{s.maxDownloads ? `/${s.maxDownloads}` : ''}</span>
                      {s.expiresAt && <span>有效期至 {formatDate(s.expiresAt)}</span>}
                      <span>{formatBytes(s.size)}</span>
                    </p>
                  </div>
                  <a href={s.shareUrl} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline flex-shrink-0">打开</a>
                  <button onClick={() => revokeShare(s.code)} className="text-xs text-red-400 hover:underline flex-shrink-0">撤销</button>
                </div>
              ))}
            </div>
          )}
      </section>

      {/* WebDAV 提示 */}
      <section className="bg-card/80 border border-border-soft rounded-2xl p-6 animate-fadeInUp soft-shadow">
        <h3 className="font-semibold text-text mb-2 flex items-center gap-2"><span className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10"><RefreshCcw className="w-4 h-4 text-primary" /></span> WebDAV</h3>
        <p className="text-sm text-text-muted">
          WebDAV 将在 M3 里程碑提供，届时可用系统账号挂载到 Windows / macOS / Linux / rclone。
        </p>
      </section>
    </div>
  );
}
